import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  CalendarToday,
  Refresh,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Receipt,
} from '@mui/icons-material';
import { financeService } from '../../services/financeService';
import { DayBook, DayBookEntry } from '../../types/finance';
import {
  ReportHeader,
  ExportToolbar,
  IndianAmountDisplay,
} from '../../components/finance';

interface EntryRowProps {
  entry: DayBookEntry;
}

const EntryRow: React.FC<EntryRowProps> = ({ entry }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, '&:hover': { bgcolor: 'grey.50' } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {entry.entry_number}
        </TableCell>
        <TableCell>{entry.description}</TableCell>
        <TableCell>{entry.reference || '-'}</TableCell>
        <TableCell align="right">
          <IndianAmountDisplay amount={entry.total_debit} />
        </TableCell>
        <TableCell align="right">
          <IndianAmountDisplay amount={entry.total_credit} />
        </TableCell>
        <TableCell>
          <Chip
            label={entry.status}
            size="small"
            color={
              entry.status === 'POSTED' ? 'success' :
              entry.status === 'DRAFT' ? 'warning' : 'default'
            }
          />
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {entry.created_by_name}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Line Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account Code</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell align="right">Debit (₹)</TableCell>
                    <TableCell align="right">Credit (₹)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(entry.accounts || []).map((acc, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{acc.account_code}</TableCell>
                      <TableCell>{acc.account_name}</TableCell>
                      <TableCell align="right">
                        {acc.debit_amount > 0 && <IndianAmountDisplay amount={acc.debit_amount} />}
                      </TableCell>
                      <TableCell align="right">
                        {acc.credit_amount > 0 && <IndianAmountDisplay amount={acc.credit_amount} />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const DayBookPage: React.FC = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dayBook, setDayBook] = useState<DayBook | null>(null);

  const fetchDayBook = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financeService.getDayBook(date);
      setDayBook(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load day book');
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (days: number) => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + days);
    setDate(currentDate.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
            Day Book
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all journal entries for a specific date
          </Typography>
        </Box>
        {dayBook && (
          <ExportToolbar
            onExportPDF={() => {}}
            onExportExcel={() => {}}
            disabled={loading}
          />
        )}
      </Box>

      {/* Date Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigateDate(-1)}
              fullWidth
            >
              ← Prev
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Date"
              type="date"
              size="small"
              fullWidth
              value={date}
              onChange={(e) => setDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigateDate(1)}
              fullWidth
            >
              Next →
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setDate(new Date().toISOString().split('T')[0])}
              fullWidth
              startIcon={<CalendarToday />}
            >
              Today
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={fetchDayBook}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            >
              Load Day Book
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : dayBook ? (
        <>
          <ReportHeader
            companyName="Your Company Name"
            reportTitle="Day Book"
            subtitle={formatDate(dayBook.date)}
          />

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Entries
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {dayBook.totals.entryCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ bgcolor: 'success.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Debit
                  </Typography>
                  <IndianAmountDisplay
                    amount={dayBook.totals.totalDebit}
                    bold
                    size="large"
                    color="positive"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ bgcolor: 'error.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Credit
                  </Typography>
                  <IndianAmountDisplay
                    amount={dayBook.totals.totalCredit}
                    bold
                    size="large"
                    color="negative"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Entries Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', width: 50 }} />
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Entry No.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reference</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Debit (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Credit (₹)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dayBook.entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No entries found for this date
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dayBook.entries.map((entry) => (
                    <EntryRow key={entry.id} entry={entry} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Receipt sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a date and click Load to view day book
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DayBookPage;
